<template>
  <div class="recruiting-performance">
    <div
      v-if="pageLoading"
    >
      <div class="recruiting-line-chart-skeleton">
        <LineChartWidgetSkeleton />
      </div>
      <v-skeleton-loader
        type="table"
      />
    </div>

    <div v-else>
      <InsightHeader
        :loading="pageLoading"
        :reporting-message="reportingMessage"
        @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
      />
      <ChartCard class="recruiting-performance-card pt-0">
        <div class="metric-summary-container">
          <MetricsSummary
            height="135px"
            width="100%"
            label-width="90px"
            :summaries="summaries"
            new-version
          />
        </div>

        <div class="pa-3">
          <RecruitingPerformanceChart
            :period-data="periodData"
          />
        </div>
      </ChartCard>

      <div class="mt-4">
        <ChartCard class="recruiting-performance-list-card">
          <RecruitingPerformanceJobs
            v-if="anyFilterApplied"
            :documents="documents"
            :is-shared-page="isSharedPage"
          />
          <RecruitingPerformanceDepartments
            v-else
            :type="summary.type"
            :table="summary.table"
            @filter-department="onFilterDepartment"
          />
        </ChartCard>
        <Pagination
          v-if="anyFilterApplied && totalPages > 1"
          :model-value="page"
          :length="totalPages"
          @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router: $router })"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import Pagination from '@/components/shared/Pagination.vue';
import { getQueryParamsFromURL } from '@/helpers/routing';
import MetricsSummary from '@/components/shared/MetricsSummary.vue';
import { numberWithCommas } from '@/helpers/formatters';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppPagination } from '@/stores/app/pagination';
import { useInsights } from '@/stores/insights/insights';
import { useRecruitingPerformance } from '@/stores/insights/recruitingPerformance';
import RecruitingPerformanceChart from '@/components/insights/OperationalInsights/RecruitingPerformance/RecruitingPerformanceChart.vue';
import RecruitingPerformanceJobs from '@/components/insights/OperationalInsights/RecruitingPerformance/RecruitingPerformanceJobs.vue';
import RecruitingPerformanceDepartments from '@/components/insights/OperationalInsights/RecruitingPerformance/RecruitingPerformanceDepartments.vue';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import { useDatePicker } from '@/stores/filters/datePicker';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useColors } from '@/composables/colors';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import LineChartWidgetSkeleton from '@/components/insights/skeletonLoaders/chartSkeletons/LineChartWidgetSkeleton.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import { useInsightsShare } from '@/stores/insights/share';

export default {
  name: 'RecruitingPerformance',

  components: {
    MetricsSummary,
    Pagination,
    RecruitingPerformanceChart,
    RecruitingPerformanceDepartments,
    RecruitingPerformanceJobs,
    InsightHeader,
    LineChartWidgetSkeleton,
    ChartCard,
  },
  emits: ['track-page-load'],
  setup() {
    const { brandPrimary, green500 } = useColors();

    return { brandPrimary, green500 };
  },
  data() {
    return {
      isSharedPage: false,
    };
  },
  computed: {
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useAppPagination, ['page', 'totalPages']),
    ...mapState(useInsights, ['reportingMessage']),
    ...mapState(useFilters, ['mergedTempFilters']),
    ...mapState(useDatePicker, ['sortedRange']),
    ...mapState(useRecruitingPerformance, [
      'totals', 'documents', 'periodData', 'summary',
    ]),
    ...mapState(useAppBase, ['browserBackOrForwardNavigation']),

    summaries() {
      const {
        totals, brandPrimary, green500, sortedRange,
      } = this;
      return [
        {
          id: 'average-days-live',
          label: 'Average Days Live',
          value: numberWithCommas(Math.floor(totals.average_days_published)),
          color: green500,
          tooltip: {
            html: `Average number of days a job post was live on career page.
                  Calculated using closed job posts.`,
            key: 'recruiting-performance-average-days-live',
          },
        },
        {
          id: 'job-posts-open',
          label: 'Job Posts Open',
          value: numberWithCommas(Math.floor(totals.total_jobs_open)),
          color: brandPrimary,
          tooltip: {
            html: `Total number of job posts that were open before ${sortedRange[0]}
                  and closed after ${sortedRange[1]}.`,
            key: 'recruiting-performance-job-posts-open',
          },
        },
        {
          id: 'job-posts-added',
          label: 'Job Posts Added',
          value: numberWithCommas(Math.floor(totals.total_jobs_added)),
          color: brandPrimary,
          tooltip: {
            html: `Total number of job posts that were added to career page
                  from ${sortedRange[0]} to ${sortedRange[1]}.`,
            key: 'recruiting-performance-job-posts-added',
          },
        },
        {
          id: 'job-posts-removed',
          label: 'Job Posts Removed',
          value: numberWithCommas(Math.floor(totals.total_jobs_removed)),
          color: brandPrimary,
          tooltip: {
            html: `Total number of job posts that were removed from career page
                  from ${sortedRange[0]} to ${sortedRange[1]}.`,
            key: 'recruiting-performance-job-posts-removed',
          },
        },
        {
          id: 'average-score',
          label: 'Average Score',
          value: numberWithCommas(Math.floor(totals.average_score)),
          color: brandPrimary,
          tooltip: {
            html: `Jobs that score 85+ are proven to attract more qualified
                  and diverse candidate pools.`,
            key: 'recruiting-performance-average-score',
            title: 'Performance Score:',
          },
        },
      ].filter(Boolean);
    },

    anyFilterApplied() {
      const { filters, myJobsEnabled } = this.routeQueryFilters(this.$route.query) || {};
      if (myJobsEnabled?.value) {
        return true;
      }

      return filters.some((filter) => filter.id !== 'page');
    },
  },

  watch: {
    browserBackOrForwardNavigation(newValue) {
      /**
       * Verifies the correct path in case this is triggered
       * on a back button click to a different page
       */
      if (newValue && window.location.pathname === '/app/insights/recruiting-performance') {
        const searchParams = getQueryParamsFromURL();
        const [from, to] = this.sortedRange || [];

        this.loadPage({
          router: this.$router,
          page: searchParams?.page || 1,
          selectedMyJobs: searchParams?.owned === 'true',
          selectedDateRange: {
            from,
            to,
          },
          selectedFilters: this.mergedTempFilters,
        });
      }
    },
  },

  async mounted() {
    this.setShowInternalDefault(true);

    subscribeToFilterUpdates(this.loadPage);

    this.$emit('track-page-load', { page: 'Recruiting Performance' });
    this.setFilterConfig({
      module: 'analytics/recruitingPerformance',
      datepicker: true,
      showMyJobs: !window?.sessionStorage?.reportingShareKey,
      subMenuType: null,
    });

    this.updateFilters(this.routeQueryFilters(this.$route.query));

    const isSharedPage = window.sessionStorage.reportingShareKey;

    this.isSharedPage = Boolean(isSharedPage);

    this.showFilterBarSettings({ route: this.$route.name, options: [] });

    if (!isSharedPage) {
      this.setShareConfig({
        shareable: true,
      });
    }

    await this.loadPage({
      router: this.$router,
      page: this.$route.query?.page || 1,
      routeQuery: this.$route.query,
      selectedMyJobs: this.$route.query?.owned === 'true',
    });
  },

  beforeUnmount() {
    unsubscribeToFilterUpdates();

    this.clearPersistentFilters();

    this.resetShareConfig();
  },

  methods: {
    ...mapActions(useInsights, ['setReportingMessage', 'setShowInternalDefault']),
    ...mapActions(useRecruitingPerformance, ['loadPage']),
    ...mapActions(useFilters, ['sendFilterRequest', 'updateFilters', 'clearPersistentFilters', 'setFilterConfig']),
    ...mapActions(useAppBase, ['showFilterBarSettings']),
    ...mapActions(useInsightsShare, ['setShareConfig', 'resetShareConfig']),
    routeQueryFilters(query) {
      const {
        from, to, owned, ...filterValues
      } = query || {};

      const filters = Object.keys(filterValues).map((id) => {
        const value = Array.isArray(filterValues[id]) ? filterValues[id] : [filterValues[id]];
        const valueObjectArray = value.map((val) => ({ id: val, label: val }));

        return { id, value: valueObjectArray, options: valueObjectArray };
      });

      return { dateRange: { from, to }, myJobsEnabled: { value: owned }, filters };
    },

    async onFilterDepartment({ department_id: department, tag_id: tag }) {
      const queryUpdate = this.summary.type === 'department'
        ? { department, page: undefined }
        : { tag_job_family: tag, page: undefined };

      const newQuery = { ...this.$route.query, ...queryUpdate };

      this.updateFilters(this.routeQueryFilters(newQuery));
      this.sendFilterRequest({ router: this.$router });
    },
  },
};
</script>

<style lang="less" scoped>
.recruiting-line-chart-skeleton {
  background: @white;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
}

.recruiting-performance :deep(.summary-container) {
  box-shadow: none;
}

.recruiting-performance-card {
  border-radius: 8px;
  border: 1px solid @stroke-default;
  box-shadow: none;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.recruiting-performance-list-card {
  box-shadow: none;
  border-radius: 8px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.metric-summary-container {
  border-bottom: 1px solid @stroke-default;

  :deep(.new-version) {
    border: none !important;
    border-radius: 8px 8px 0 0;
  }
}

.header-skeleton-loader {
  width: 350px;
}

.content-skeleton-loader {
  width: 800px;
}

.header-skeleton-loader :deep(.v-skeleton-loader__bone),
.content-skeleton-loader :deep(.v-skeleton-loader__bone) {
  margin: 0 !important;
}
</style>
